/**
 * Single PlayerPage projection adapter.
 *
 * The active PlayerPage path calls the owner-only
 * GET /api/v2/teams/{team}/players/{character_sheet}/weekly-projection/
 * endpoint. Its input is the EXACT league season/week NFLProjectionMirror
 * stat line, NOT TierDefinition/PlayerTierAverages or baked 2025 fixture data.
 * The existing Python scoring engine supplies base points, equipped points,
 * stat lines, and leave-one-out gear attribution. The browser only formats
 * the response and subtracts the two server scores.
 *
 * The older historical fixture helpers below remain for their original,
 * not-yet-implemented GearPage/GearItemCard use cases; PlayerPage does not
 * call them. Never mix historical tier estimates into the live weekly result.
 *
 * Failures reject; an explicit available:false represents genuinely missing
 * data. Never display a missing projection as zero or mix sources.
 */

import { getDraftPoolPlayers } from '../api/draftApi';
import { getPlayerGearProjection } from '../api/playerApi';
import {
    getHistoricalProjection,
    HISTORICAL_PROJECTION_SOURCE,
} from '../data/historicalProjection2025';
import {
    getGearProjection,
    GEAR_PROJECTION_SOURCE,
} from '../data/gearProjectionImpact';

/** Positions the historical tier system covers. */
export const PROJECTED_POSITIONS = ['QB', 'RB', 'WR'];

/**
 * Load `position_rank` for every player in the team's draft pool.
 *
 * Uses the EXISTING draft-pool endpoint — `DraftPoolPlayersView` already
 * returns gsis_id / position / position_rank, so no backend work is needed to
 * rank a roster player. Rosters expose `gsis_id` too, which is the join key.
 *
 * @param {number|string} teamId
 * @returns {Promise<Map<string, {position: string, rank: number|null}>>}
 *
 * PARTIAL FAILURE IS TOLERATED BY DESIGN. One position's pool failing (e.g. no
 * QB pool generated for the week) must not blank out the whole tab, so each
 * request is settled independently and failures are logged and skipped. A
 * player absent from the map simply has no projection, which renders as —.
 * This is NOT silent-failing on unexpected data: the rejection is surfaced in
 * the console with its position, and the caller still gets a well-formed map.
 */
export async function loadRankByGsis(teamId) {
    if (teamId == null) {
        throw new Error(
            'loadRankByGsis() requires a teamId. Received ' + JSON.stringify(teamId) +
            '. The projection lookup cannot resolve draft-pool ranks without one.'
        );
    }

    const results = await Promise.allSettled(
        PROJECTED_POSITIONS.map((position) => getDraftPoolPlayers(teamId, position))
    );

    const rankByGsis = new Map();

    results.forEach((result, index) => {
        const position = PROJECTED_POSITIONS[index];

        if (result.status === 'rejected') {
            console.error(
                `⚠️ playerProjection: draft pool for ${position} could not be read; ` +
                `players at that position will show projections as unavailable.`,
                result.reason
            );
            return;
        }

        // ⚠️ A 200 WITH THE WRONG SHAPE IS A CONTRACT VIOLATION, NOT AN EMPTY
        // POOL — AND MUST NOT BE SILENTLY DEGRADED.
        //
        // This used to `console.error` and `return`, which quietly converted a
        // broken API into "every player at this position happens to be
        // unrankable". That is indistinguishable, on screen, from the real and
        // legitimate unranked case, so a serialiser regression or a renamed
        // field would ship looking exactly like normal missing data — the
        // silent-failure pattern AGENTS.md forbids.
        //
        // A REJECTED request is different and is still tolerated above: a 403
        // or a down endpoint is an expected operational condition with a known
        // meaning. A 200 whose body we cannot parse is a bug, so it throws with
        // the position and payload named.
        const players = result.value?.all_players;
        if (!Array.isArray(players)) {
            throw new Error(
                `playerProjection.loadRankByGsis(): draft pool response for ` +
                `${position} returned HTTP 200 but carried no \`all_players\` ` +
                `array (received ${JSON.stringify(result.value)}). This is an API ` +
                'contract violation, not an empty pool — refusing to present it ' +
                'as "no ranks available".'
            );
        }

        players.forEach((player) => {
            if (!player.gsis_id) return;
            rankByGsis.set(player.gsis_id, {
                position: player.position,
                rank: typeof player.position_rank === 'number' ? player.position_rank : null,
            });
        });
    });

    return rankByGsis;
}

/**
 * Resolve one player's projection context (rank → tier → estimated stats/points).
 *
 * ⚠️ **NOT PART OF THE PLAYERPAGE PATH ANY MORE.** This reads the baked 2025
 * tier fixture and is retained only for the unbuilt §11 `GearPage` /
 * `GearItemCard` coarse-estimate surfaces — see the module header. The GEAR
 * tab resolves position/tier/rank SERVER-side via `buildProjectionSummary()`,
 * so that the tier the UI names is the same one the engine actually scored.
 *
 * @param {Object} params
 * @param {string} params.gsisId
 * @param {'QB'|'RB'|'WR'} params.position
 * @param {Map} params.rankByGsis - from `loadRankByGsis()`
 * @returns {Object|null} `{ source, isMock, rank, tier, estimatedPoints,
 *          estimatedStats }`, or null when this player cannot be projected.
 *
 * Returning null is a normal, expected outcome (unranked player, position
 * outside QB/RB/WR, rank beyond the configured bands). Callers render — for it.
 */
export function getPlayerProjectionContext({ gsisId, position, rankByGsis }) {
    if (!rankByGsis) {
        throw new Error(
            'getPlayerProjectionContext() requires a rankByGsis map from ' +
            'loadRankByGsis(). Received ' + JSON.stringify(rankByGsis) + '.'
        );
    }

    if (!gsisId) return null;

    const entry = rankByGsis.get(gsisId);
    const rank = entry?.rank ?? null;
    if (rank == null) return null;

    // Trust the roster's position, falling back to the pool's copy of it.
    const effectivePosition = position || entry?.position || null;
    if (!effectivePosition) return null;

    // ⚠️ A RANK IS ONLY MEANINGFUL WITHIN ITS OWN POSITION.
    //
    // `position_rank` is "Nth best QB", not a global ordering. If the roster
    // says this player is a WR while the pool ranked them as a QB, pairing the
    // roster's position with the pool's rank fabricates a THIRD thing that
    // exists in neither source — e.g. WR #2's tier applied to a player the pool
    // considers QB #2. That silently resolves to a confident, wrong projection
    // rather than an obvious failure.
    //
    // The mismatch is real, not theoretical: a player can be repositioned, and
    // the per-TEAM `current_draft_catalog` means the pool a rank came from is
    // not guaranteed to agree with the current roster row.
    //
    // We do NOT guess which source is right — that is exactly the kind of
    // invention this adapter exists to prevent. Disagreement means the rank
    // cannot be trusted for this player, so we return null and the UI renders
    // an honest "unavailable".
    if (entry?.position && entry.position !== effectivePosition) {
        console.error(
            '⚠️ playerProjection: roster position disagrees with the draft-pool ' +
            'position for this player, so their rank cannot be interpreted. ' +
            'Projection reported as unavailable rather than guessing a tier.',
            {
                gsisId,
                rosterPosition: effectivePosition,
                poolPosition: entry.position,
                rank,
            }
        );
        return null;
    }

    const historical = getHistoricalProjection(effectivePosition, rank);
    if (!historical) return null;

    return {
        source: `historical-${HISTORICAL_PROJECTION_SOURCE.year}`,
        isMock: HISTORICAL_PROJECTION_SOURCE.isMock === true,
        position: effectivePosition,
        rank,
        tier: historical.tier,
        estimatedPoints: historical.estimatedPoints,
        estimatedStats: historical.stats,
    };
}

/**
 * Sum the baked point differentials for a set of gear items on one player.
 *
 * ⚠️ **NOT PART OF THE PLAYERPAGE PATH ANY MORE.** The GEAR tab's point delta
 * is `geared_points - base_points` from one server-side engine run; this
 * baked-differential sum is retained only for the unbuilt §11 surfaces (see
 * the module header). Its null-vs-zero contract below is still live and still
 * load-bearing for those surfaces, and is pinned by `playerProjection.test.js`.
 *
 * @param {Array<Object>} gearItems - owned-gear payloads
 * @param {Object|null} context - from `getPlayerProjectionContext()`
 * @returns {{ total: number|null, items: Array, unknownCount: number }}
 *
 * ============================================================================
 * NULL vs ZERO — THE THIRD SITE OF THE SAME CONTRACT. READ BEFORE EDITING.
 * ============================================================================
 * `total` distinguishes three genuinely different situations:
 *
 *   • **Empty loadout** → `0`. Wearing no gear is a FULLY KNOWN state whose
 *     adjustment is exactly zero. It is not a mystery, and must render as
 *     "+0.00pts", not "—".
 *   • **Some/all items resolve** → their sum. Items lacking projection data are
 *     excluded and counted in `unknownCount` so the UI can disclose partial
 *     coverage rather than quietly understating the impact.
 *   • **Non-empty loadout, nothing resolvable** → `null`. Here we truly cannot
 *     say what the gear does, and the UI shows "—".
 *
 * ⚠️ THE EMPTY CASE USED TO RETURN NULL. The guard was `knownCount > 0 ? total
 * : null`, and an empty array leaves `knownCount` at 0 — so a player wearing no
 * gear, and every player immediately after pressing **Clear**, reported
 * "Gear: —" and "Points: —" as if the adjustment were unknowable. It is not:
 * it is zero, exactly and knowably. That contradicted this feature's own manual
 * plan (§7's Clear expectation) and repeated the same mistake already fixed for
 * analyzed-neutral catalog rows in `gearProjectionImpact.js`.
 *
 * The distinguishing question is NOT "did anything resolve?" but "was there
 * anything to resolve?".
 */
export function sumGearProjection(gearItems, context) {
    const items = [];
    let total = 0;
    let knownCount = 0;
    let unknownCount = 0;

    const equipped = gearItems || [];

    if (!context) {
        // No projection context at all (unrankable player, opponent view, …).
        // Even an empty loadout is unknown here: without a position/tier we
        // cannot claim ANY figure, including zero.
        return { total: null, items: [], unknownCount: equipped.length };
    }

    // EMPTY LOADOUT = A KNOWN ZERO. Handled before the loop so the
    // `knownCount` heuristic below can never mistake "nothing equipped" for
    // "nothing resolvable" — two different facts that must not share a value.
    if (equipped.length === 0) {
        return { total: 0, items: [], unknownCount: 0 };
    }

    equipped.forEach((gear) => {
        const impact = getGearProjection(gear, context.position, context.tier);

        if (impact == null) {
            unknownCount += 1;
        } else {
            total += impact;
            knownCount += 1;
        }

        items.push({
            id: gear.id,
            itemId: gear.item_id ?? null,
            name: gear.name,
            description: gear.description,
            slot: gear.slot,
            impact, // null = unknown, 0 = analyzed-and-neutral
        });
    });

    return {
        // Reaching here means at least one item IS equipped, so `knownCount`
        // now answers the right question: did any of them resolve?
        total: knownCount > 0 ? total : null,
        items,
        unknownCount,
    };
}

/**
 * Round a derived figure back to the server's 2-decimal precision.
 *
 * Only ever applied to a DIFFERENCE of two values the server already rounded.
 * `34.79 - 27.31` is `7.479999999999997` in IEEE 754, and rendering that as
 * `+7.48pts` is right while carrying the noise into any further arithmetic is
 * not. `null` passes through untouched — rounding must never manufacture a
 * zero out of an unknown.
 */
function roundToServerPrecision(value) {
    if (value == null) return null;
    return Math.round(value * 100) / 100;
}

/**
 * The honest "nothing could be projected" shape.
 *
 * Every numeric field is `null`, never a fabricated zero, and the caller never
 * has to null-check field by field — `available: false` is the single gate.
 */
function unavailableSummary({ reason, isOpponentView = false, equippedCount = 0 }) {
    return {
        available: false,
        isMock: false,
        reason,
        // Lets the UI soften its copy for the expected read-only case rather
        // than presenting a deliberate product boundary as a data problem.
        unavailableBecauseOpponentView: isOpponentView === true,
        basePoints: null,
        gearPoints: null,
        totalPoints: null,
        estimatedStats: null,
        gearedStats: null,
        statLineChanged: null,
        gearItems: [],
        unknownGearCount: equippedCount,
        totalIsPartial: false,
        warnings: [],
        source: null,
    };
}

/**
 * Build the full projection summary for a player and their equipped loadout.
 *
 * ⭐ **THE ONE SEAM.** Every projected number on PlayerPage — the GEAR tab's
 * two stat lines, its `Base | Gear | Total` bar, and the always-visible PROJ
 * figure — comes from one call to this function. `PlayerPage` makes that call
 * once and passes the result to the tab, so the two surfaces cannot disagree:
 * they are rendering the same object, not two independently-derived ones.
 *
 * ⚠️ **ASYNC, AND SERVER-AUTHORITATIVE.** This used to compose two baked
 * fixtures synchronously. It now awaits
 * `GET /v2/teams/{team}/players/{sheet}/weekly-projection/`, which
 * reads this league's current-week NFLProjectionMirror projected stat line and
 * uses the canonical scorer with and without equipped gear.
 *
 * **NO GEAR RULE IS APPLIED HERE.** The only arithmetic in this function is
 * `geared_points - base_points`, a difference of two figures the server
 * produced in the same engine run, from the same catalog, in the same year.
 * If you find yourself reaching for a multiplier or a stat table in this file,
 * the payload is wrong — fix the contract, do not reimplement scoring in JS.
 *
 * @param {Object} params
 * @param {Object} params.player - the roster row. Must carry
 *        `character_sheet_id`; `player.id` is a DIFFERENT table's key and is
 *        never substituted (the ranges overlap, so the substitution succeeds
 *        against the wrong player rather than failing).
 * @param {number|string} params.teamId
 * @param {Array} params.equippedGear - this player's equipped rows. Their ids
 *        are sent explicitly, so the projection describes exactly the loadout
 *        the HELD list is showing. An empty array is sent as an explicitly
 *        empty loadout, which the contract distinguishes from "absent".
 * @param {boolean} [params.isOpponentView] - when true, no request is made at
 *        all: the endpoint is owner-only and would 403 by design.
 *
 * @returns {Promise<Object>} always resolves to a summary object.
 *          REJECTS on a transport failure or a contract violation — a broken
 *          read must not be indistinguishable from an honest "no data".
 */
export async function buildProjectionSummary({
    player,
    teamId,
    equippedGear,
    isOpponentView = false,
}) {
    const equipped = equippedGear || [];

    // WHY THE OPPONENT CASE SHORT-CIRCUITS BEFORE THE REQUEST. The endpoint is
    // owner-only (`403`, mirroring `DraftPoolPlayersView`), so asking would
    // mean one guaranteed failure and a red console entry on a perfectly
    // ordinary screen, ending in exactly the state we return here. The reason
    // is named explicitly because every unavailable state looks identical to a
    // user otherwise — and "no tier data for this player" would be a false
    // explanation when the real cause is a deliberate product boundary.
    if (isOpponentView) {
        return unavailableSummary({
            reason:
                'Projections are only available for your own team. Another ' +
                'manager’s player projection cannot be looked up.',
            isOpponentView: true,
            equippedCount: equipped.length,
        });
    }

    const characterSheetId = player?.character_sheet_id ?? null;
    if (characterSheetId == null) {
        // FAIL CLOSED RATHER THAN GUESS A TARGET. Without a CharacterSheet id
        // there is no player to project. Falling back to `player.id` would
        // produce a confident projection OF SOMEBODY ELSE, because the two
        // tables' id ranges overlap and the server would resolve it happily.
        return unavailableSummary({
            reason:
                'This player’s record is missing its character sheet, so their ' +
                'projection cannot be looked up.',
            equippedCount: equipped.length,
        });
    }

    if (teamId == null) {
        throw new Error(
            'buildProjectionSummary() requires a teamId. Received ' +
            JSON.stringify(teamId) + '. The projection endpoint is team-scoped ' +
            'and ownership is checked against it.'
        );
    }

    let data;
    try {
        data = await getPlayerGearProjection(teamId, characterSheetId, {
            // EXPLICIT IDS, NOT "whatever the server thinks is equipped".
            // `PlayerPage` re-reads the inventory from the server after every
            // write, so these ARE server truth — and sending them means the
            // projection and the HELD list on screen describe the same set by
            // construction. `[]` is deliberately sent as an explicitly empty
            // loadout; the contract does not collapse it with "absent".
            gearIds: equipped.map((gear) => gear.id),
        });
    } catch (error) {
        // A 403 IS THE OWNER-ONLY BOUNDARY, NOT A FAILURE. It is the same
        // product rule the opponent branch above anticipates, reached here
        // when a caller's view mode and the server's ownership view disagree
        // (a stale `viewMode`, a team handed over). Rendering the designed
        // panel is correct; every OTHER error is a real fault and propagates.
        if (error?.status === 403) {
            return unavailableSummary({
                reason:
                    'Projections are only available for your own team. Another ' +
                    'manager’s player projection cannot be looked up.',
                isOpponentView: true,
                equippedCount: equipped.length,
            });
        }
        throw error;
    }

    if (data.available !== true) {
        // A LEGITIMATE ABSENCE, STATED BY THE SERVER. Weekly projection rows,
        // provider-supplied stats, or required canonical scorer inputs may be
        // missing. Do not invent zeros or silently substitute tier averages.
        return unavailableSummary({
            reason:
                data.reason ||
                'No projection is available for this player’s position and rank.',
            equippedCount: equipped.length,
        });
    }

    const basePoints = data.base_points;
    const gearedPoints = data.geared_points;

    if (typeof basePoints !== 'number' || typeof gearedPoints !== 'number') {
        throw new Error(
            'buildProjectionSummary(): the projection reported `available: true` ' +
            'but carried non-numeric points (base_points=' +
            JSON.stringify(basePoints) + ', geared_points=' +
            JSON.stringify(gearedPoints) + '). An available projection must have ' +
            'real numbers — refusing to render a contradictory payload.'
        );
    }

    const serverItems = Array.isArray(data.gear_items) ? data.gear_items : [];
    const knownItemCount = serverItems.filter(
        (item) => item.projection_known === true
    ).length;

    /**
     * ⚠️ NULL vs ZERO — THE RULE THIS WHOLE FEATURE TURNS ON.
     *
     *   • **Empty loadout** → `0`. Wearing nothing is a FULLY KNOWN state
     *     whose adjustment is exactly zero, and the server returns
     *     `geared == base` for it. It must render "+0.00pts", never "—".
     *   • **Some items known** → the real difference, with `unknownGearCount`
     *     disclosing that the figure understates the loadout.
     *   • **Non-empty loadout, NOTHING known** → `null`. Here we genuinely
     *     cannot say what the gear does. The server excludes unknown items
     *     from `geared_points`, so the difference would be a tidy `0.00` —
     *     and presenting "your gear is worth exactly nothing" when the truth
     *     is "we could not analyse any of it" is the fabricated-zero bug this
     *     contract exists to prevent.
     */
    const nothingResolvable = serverItems.length > 0 && knownItemCount === 0;
    const gearPoints = nothingResolvable
        ? null
        : roundToServerPrecision(gearedPoints - basePoints);

    return {
        available: true,
        // The engine is the source now; there is no stand-in fixture left on
        // this path. Retained so the UI's placeholder banner stays wired for
        // any future provisional source rather than being deleted and forgotten.
        isMock: false,
        position: data.position,
        rank: data.rank,
        tier: data.tier,

        basePoints,
        gearPoints,
        // A null gear total must not poison the player's real base estimate —
        // "we know the base, we don't know the gear" is still a usable total.
        totalPoints: nothingResolvable ? basePoints : gearedPoints,

        /** The yellow line: the real weekly NFL projection BEFORE gear. */
        estimatedStats: data.stat_line_base,

        /**
         * The green line: the same stat line WITH gear applied.
         *
         * Carries the identical key set to `estimatedStats` so the two are
         * diffable field by field, and is rendered by the SAME
         * `formatStatLine(..., STAT_MODE_PROJECTED)` call — identical format
         * is the point. Only `RAW_STAT_MODIFICATION` gear moves these values;
         * multiplier/base-point/threshold effects move POINTS with the line
         * untouched, which is what `statLineChanged` exists to explain.
         */
        gearedStats: data.stat_line_geared,

        /**
         * Did ANY stat value actually change? Decided server-side on unrounded
         * values and never re-derived here — a change smaller than the
         * displayed precision is still a change, and a client-side comparison
         * of two rounded lines would silently lose it.
         */
        statLineChanged: data.stat_line_changed,

        gearItems: serverItems.map((item) => ({
            id: item.gear_id,
            itemId: item.item_id ?? null,
            name: item.name,
            description: item.description,
            // `null` = could not be projected at all (renders —).
            // `0` = analysed and inert (renders +0.00pts). Never conflate.
            impact: item.points_delta,
            projectionKnown: item.projection_known === true,
            // Drives the per-item copy: a stat change, or the explicit
            // "affects points, not stats" line.
            statEffects: item.stat_effects || [],
            nonStatEffects: item.non_stat_effects || [],
        })),

        unknownGearCount: data.unknown_gear_count ?? 0,

        // Nonzero generic 2PT projections have no passing/rushing/receiving
        // split. The backend scores supported inputs only and declares the
        // displayed points partial, rather than silently inventing a split.
        partialScoringInputs: data.partial_scoring_inputs || [],

        /**
         * IS `totalPoints` THE WHOLE STORY, OR ONLY PART OF IT?
         *
         * ⚠️ A PARTIAL TOTAL IS NOT A WRONG NUMBER — IT IS A RIGHT NUMBER THAT
         * MEANS SOMETHING DIFFERENT, and rendering the two identically is what
         * misleads. Unknown items are EXCLUDED from `geared_points` rather
         * than guessed, so a mixed loadout yields a real figure that
         * nonetheless UNDERSTATES what the player is wearing. Displayed bare —
         * "PROJ 18.42" — it reads as a complete, confident projection.
         *
         * The GEAR tab discloses this in prose; the always-visible score bar
         * has no room for a sentence and marks the value instead. Stated once
         * here so no surface has to re-derive it from `unknownGearCount`.
         *
         * `false` means every equipped item was accounted for — including the
         * empty-loadout case, which is a known zero, not an absence.
         */
        totalIsPartial: (data.unknown_gear_count ?? 0) > 0 || (data.partial_scoring_inputs?.length ?? 0) > 0,

        /** Non-fatal operator-facing notes. Never used to mask an error. */
        warnings: data.warnings || [],

        source: data.source || null,
    };
}
